import { useState } from 'react'
import { utils, produce, dayjs } from 'passiv-libs'
import { CPI, HUM } from 'passiv-api'

const capability = utils.account.hasCapability('immersionhotwater')
  ? 'immersionhotwater'
  : 'heating'

const zone = utils.account.hasCapability('multizone') ? 1 : 1

const AwayContainer = ({ render }) => {
  const { heating } = window.__STORE__

  const [state, setState] = useState({
    loading: false,
    show: false,
    start: { open: false, value: dayjs().startOf('hour').toISOString() },
    end: { open: false, value: dayjs().add(3, 'day').startOf('hour').toISOString() },
    inSetpoint: 0,
    modal: {
      open: '' // 'returning' // 'going'
    }
  })

  const openContainer = () => {
    utils.settings.setPortalWindowStatus(true)
    const { start, end } = heating.zone1.occupancy_away
    setState(
      produce(draft => {
        if (start) draft.start.value = start
        if (end) draft.end.value = end
        draft.start.open = false
        draft.end.open = false
        draft.show = true
      })
    )
  }

  const closeContainer = () => {
    utils.settings.setPortalWindowStatus(false)
    setState(
      produce(draft => {
        draft.show = false
      })
    )
  }

  const toggle = () => {
    const isAwayNow = utils.heating.isAwayNow(heating.zone1.occupancy_away)

    if (state.show) {
      closeContainer()
    } else if (isAwayNow || utils.settings.isTipDismissed('modal-tip-away')) {
      openContainer()
    } else {
      toggleModal({ id: 'going', open: true })
    }
  }

  const toggleRow = ({ id }) => {
    setState(
      produce(draft => {
        draft.start.open = false
        draft.end.open = false
        draft[id].open = !state[id].open
      })
    )
  }

  const change = ({ id, value, type }) => {
    const [hours, mins] = value.split(':')
    const _value =
      type === 'date'
        ? dayjs(value).toISOString()
        : dayjs(state[id].value).hour(hours).minute(mins).toISOString()

    setState(
      produce(draft => {
        draft[id].value = _value
      })
    )
  }

  const save = async () => {
    setState(
      produce(draft => {
        draft.loading = true
      })
    )

    try {
      await CPI.PUT({
        capability,
        zone,
        property: 'occupancy_away',
        payload: {
          start: dayjs(state.start.value).utc().format(),
          end: dayjs(state.end.value).utc().format()
        }
      })

      setState(
        produce(draft => {
          draft.show = false
        })
      )
      if (utils.settings.canShowAppRateModal()) utils.general.triggerRateApp()
    } catch (error) {}

    setState(
      produce(draft => {
        draft.loading = false
      })
    )
  }

  const _delete = async () => {
    setState(
      produce(draft => {
        draft.loading = true
      })
    )

    try {
      await CPI.DELETE({ capability, zone, property: 'occupancy_away' })

      setState(
        produce(draft => {
          draft.show = false
        })
      )

      if (utils.settings.canShowAppRateModal()) utils.general.triggerRateApp()
    } catch (error) {}

    setState(
      produce(draft => {
        draft.loading = false
      })
    )
  }

  const toggleModal = ({ id, open, inSetpoint }) => {
    setState(
      produce(draft => {
        draft.modal.open = open ? id : ''
        if (inSetpoint) draft.inSetpoint = inSetpoint
      })
    )
  }

  // NOTE: tipId must have _ underscore instead of dash
  const dismissTipInDB = async ({ tipId }) => {
    await HUM.PATCH({
      op: 'replace',
      path: `/home/${tipId}`,
      value: 'true'
    })
  }

  const data = {
    ...state,
    modal: {
      toggleModal,
      open: state.modal.open,
      going: {
        id: 'modal-tip-away',
        open: state.modal.going,
        title: 'Away',
        text: `We won't heat your home when you are away, unless it falls below your Away / Frost protect temperature.
        Your heating will resume ready for your return.`,
        tip: true,
        onClickTip: utils.settings.setTipDismissed,
        buttons: [
          {
            name: 'ok',
            text: 'OK',
            onClick: () => {
              // if tip is dismissed set it in DB
              if (utils.settings.isTipDismissed('modal-tip-away')) dismissTipInDB({tipId: "modal_tip_away"})
              openContainer()
              toggleModal({ id: 'going', open: false })
            }
          },
          {
            name: 'cancel',
            text: 'Cancel',
            onClick: () => {
              toggleModal({ id: 'going', open: false })
            }
          }
        ]
      },
      returning: {
        id: 'modal-away-returning',
        open: state.modal.returning,
        title: 'Returning Home?',
        text: `This will end your 'Away' and return your home to your 'In' set point of ${state.inSetpoint}°.`,
        tip: true,
        onClickTip: utils.settings.setTipDismissed,
        buttons: [
          {
            name: 'ok',
            text: 'OK',
            onClick: () => {
              // if tip is dismissed set it in DB
              if (utils.settings.isTipDismissed('modal-away-returning')) dismissTipInDB({tipId: "modal_away_returning"})
              _delete()
              toggleModal({ id: 'returning', open: false })
            }
          },
          {
            name: 'cancel',
            text: 'Cancel',
            onClick: () => {
              toggleModal({ id: 'returning', open: false })
            }
          }
        ]
      }
    },
    isAway: utils.heating.isAwayNow(heating?.zone1.occupancy_away),
    isAwayInFuture: utils.heating.isAwayInFuture(heating?.zone1.occupancy_away),
    onToggle: toggle,
    onToggleRow: toggleRow,
    onChange: change,
    onDelete: _delete,
    onSave: save
  }

  return render(data)
}

export default AwayContainer
